import { MarkLineComponentOption } from 'echarts';
import { TFunction } from 'i18next';
import { floodCategoryColorsSolid } from '../../waterLevel.constants';
import {
  FloodCategoryLevel,
  WaterLevelObservation,
} from '../../waterLevel.types';
import { formatNumber } from '../../../../shared/utils';

type GetCategoriesMarkLineDataArgs = {
  t: TFunction;
  categoryLevels: FloodCategoryLevel[];
  unit: string;
  getContrastText: (background: string) => string;
};

export const UPPER_LOWER_BOUNDS_PADDING_PCT = 0.2;
const MIN_HIGH_LOW_RANGE = 5;

/**
 * Category levels don't always include the "none" level, but we always want to style it
 */
const getCategoriesSortedWithNone = (
  categoryLevels: FloodCategoryLevel[],
  minYValue: number,
): FloodCategoryLevel[] => {
  const sortedCategoryLevels = [...categoryLevels].sort(
    (a, b) => a.level - b.level,
  );

  if (sortedCategoryLevels[0]?.category !== 'none') {
    return [
      {
        category: 'none',
        level: Math.min(minYValue, 0),
      },
      ...sortedCategoryLevels,
    ];
  }

  return sortedCategoryLevels;
};

/**
 * Creates markLine data for flood category thresholds.
 * Returns separate arrays for lines (below data) and labels (above data).
 */
export const getCategoriesMarkLineData = (
  args: GetCategoriesMarkLineDataArgs,
): {
  lines: MarkLineComponentOption['data'];
  labels: MarkLineComponentOption['data'];
} => {
  const { t, categoryLevels, unit, getContrastText } = args;

  if (categoryLevels.length === 0) {
    return { lines: [], labels: [] };
  }

  // Filter out 'none' category - it doesn't have a visual threshold line
  const visibleCategories = categoryLevels.filter((c) => c.category !== 'none');

  const lines = visibleCategories.map((categoryLevel) => {
    const { category, level } = categoryLevel;
    const color = floodCategoryColorsSolid[category] || '#000000';

    return {
      yAxis: level,
      symbol: ['none', 'none'],
      lineStyle: { color, width: 2, type: 'solid' as const },
      label: { show: false },
    };
  });

  const labels = visibleCategories.map((categoryLevel) => {
    const { category, level } = categoryLevel;
    const height = 16;
    const color = floodCategoryColorsSolid[category] || '#000000';
    const textColor = getContrastText(color);
    const label = t(
      `waterLevel.weatherLevelsChart.levelLabelsShort.${category}`,
    );
    const levelLabel = formatNumber(level, 1).replace('.0', '');

    return {
      yAxis: level,
      symbol: ['none', 'none'],
      lineStyle: { color: 'transparent', width: 0 },
      label: {
        show: true,
        position: 'insideStartTop' as const,
        distance: [-40, -height / 2],
        formatter: `{label|${label}}{value|${levelLabel} ${unit}}`,
        rich: {
          label: {
            backgroundColor: color,
            borderRadius: [2, 0, 0, 2],
            color: textColor,
            fontWeight: 'bold' as const,
            fontSize: 10,
            padding: [0, 2, 0, 4],
            height,
          },
          value: {
            backgroundColor: color,
            borderRadius: [0, 2, 2, 0],
            color: textColor,
            fontSize: 10,
            padding: [0, 4, 0, 2],
            height,
          },
        },
      },
    };
  });

  return { lines, labels };
};

export const getChartBounds = (
  allValues: WaterLevelObservation[],
  categories: FloodCategoryLevel[],
): { upperBounds: number; lowerBounds: number } => {
  const levels = allValues.map(({ level }) => level);
  const highestLevel = Math.max(...levels);
  const lowestLevel = Math.min(...levels);

  const sortedCategories = getCategoriesSortedWithNone(categories, lowestLevel);

  const lowestFloodCategoryLevel = Math.min(
    ...sortedCategories
      .filter((c) => c.category !== 'none')
      .map((c) => c.level),
  );
  const highestFloodCategoryLevel = sortedCategories.at(-1)?.level;

  const highestPoint = highestFloodCategoryLevel
    ? Math.max(highestLevel, highestFloodCategoryLevel)
    : highestLevel;

  const lowestPoint = lowestFloodCategoryLevel
    ? Math.min(lowestLevel, lowestFloodCategoryLevel)
    : lowestLevel;

  // Make sure we have at least a little range so we the chart isn't unrealistically zoomed in
  const chartRange = Math.max(highestPoint - lowestPoint, MIN_HIGH_LOW_RANGE);
  const padding = chartRange * UPPER_LOWER_BOUNDS_PADDING_PCT;
  const upperBounds = highestPoint + padding;

  const lowerBounds =
    lowestPoint < 0
      ? lowestPoint - padding
      : Math.max(lowestPoint - padding, 0);

  return {
    lowerBounds,
    upperBounds,
  };
};

export const observationToSeriesData = ({
  timestamp,
  level,
}: WaterLevelObservation): [Date, number] => [timestamp, level];
